import React from "react";
import { useHistory, Link, useLocation } from "react-router-dom";
import { auth } from "../firebase";
import useAuth from "../hooks/useAuth";
import InstallAppButton from "./InstallAppButton";
import { FiMap, FiUsers, FiFileText } from "react-icons/fi";
import "../styles/quotation-system.css";

export default function TopBar() {
  const history = useHistory();
  const location = useLocation();
  const { profile } = useAuth();

  // Enhanced navigation function with improved PWA standalone mode support
  const navigateTo = (path, event = null) => {
    // Stop all event propagation immediately to prevent React Router interference
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
      // Also stop default on the target element
      if (event.target) {
        event.target.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        };
      }
    }

    // Enhanced standalone mode detection
    const isStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      (window.matchMedia('(display-mode: fullscreen)').matches && 
       !window.navigator.userAgent.includes('Chrome'));

    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    // Construct absolute URL
    const baseUrl = window.location.origin;
    const normalizedPath = path.startsWith('/') ? path : '/' + path;
    const fullUrl = baseUrl + normalizedPath;
    
    console.log('Navigating to:', fullUrl, 'Standalone mode:', isStandalone, 'Platform:', isIOS ? 'iOS' : isAndroid ? 'Android' : 'Other');
    
    // Always use native navigation for critical paths in standalone mode
    // For other cases, check if we're in standalone first
    if (isStandalone || path.includes('/q/leads')) {
      // In standalone mode OR for leads pages, use native navigation ONLY
      // This ensures navigation works reliably
      
      // Use requestAnimationFrame to ensure navigation happens after all handlers
      requestAnimationFrame(() => {
        try {
          // Primary method: Use href (most reliable)
          window.location.href = fullUrl;
        } catch (e) {
          console.warn('window.location.href failed, trying assign:', e);
          try {
            // Fallback 1: Use assign
            window.location.assign(fullUrl);
          } catch (e2) {
            console.warn('window.location.assign failed, trying replace:', e2);
            try {
              // Fallback 2: Use replace
              window.location.replace(fullUrl);
            } catch (e3) {
              console.error('All navigation methods failed:', e3);
            }
          }
        }
      });
    } else {
      // Regular browser mode - use React Router for better UX
      try {
        if (history && typeof history.push === 'function') {
          history.push(path);
        } else {
          window.location.href = fullUrl;
        }
      } catch (error) {
        console.error('React Router navigation error:', error);
        window.location.href = fullUrl;
      }
    }
  };

  const onLogout = async () => {
    await auth.signOut();
    navigateTo("/login");
  };

  const onBack = () => {
    if (history.length > 1) {
      try {
        history.goBack();
      } catch (error) {
        // Fallback navigation
        if (profile?.role === "admin") {
          navigateTo("/admin");
        } else if (profile?.role === "contentwriter") {
          navigateTo("/writer/blogs");
        } else {
          navigateTo("/q");
        }
      }
    } else if (profile?.role === "admin") {
      navigateTo("/admin");
    } else if (profile?.role === "contentwriter") {
      navigateTo("/writer/blogs");
    } else {
      navigateTo("/q");
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="topbar-elegant">
      <button 
        type="button"
        onClick={onBack}
        className="btn-elegant btn-elegant-secondary"
        style={{ 
          padding: "0.5rem 1rem", 
          fontSize: "0.875rem", 
          marginRight: "1rem",
          minHeight: "48px",
          minWidth: "48px",
          touchAction: "manipulation"
        }}
      >
        ← Back
      </button>
      
      <div className="user-info">
        <span style={{ 
          width: "36px", 
          height: "36px", 
          borderRadius: "50%", 
          background: "linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #06b6d4 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontWeight: "700",
          fontSize: "0.875rem",
          marginRight: "0.5rem",
          boxShadow: "0 2px 8px rgba(139, 92, 246, 0.4)",
          flexShrink: 0
        }}>
          {profile?.name?.charAt(0)?.toUpperCase() || "U"}
        </span>
        <span>
          {profile?.role === "admin" ? "Admin" : 
           profile?.role === "contentwriter" ? "Content Writer" : 
           "Sales Executive"} - {profile?.name || "User"}
        </span>
      </div>
      
      <div className="topbar-center-stack">
        {profile?.role === "admin" && (
          <nav className="nav-links admin-nav-mobile">
            <Link 
              to="/admin" 
              className={`nav-link ${isActive("/admin") ? "active" : ""}`}
              style={isActive("/admin") ? { background: "rgba(255, 255, 255, 0.15)", color: "white" } : {}}
            >
              Quotes
            </Link>
            <Link 
              to="/admin/users" 
              className={`nav-link ${isActive("/admin/users") ? "active" : ""}`}
              style={isActive("/admin/users") ? { background: "rgba(255, 255, 255, 0.15)", color: "white" } : {}}
            >
              Users
            </Link>
            <Link 
              to="/admin/blogs" 
              className={`nav-link ${isActive("/admin/blogs") ? "active" : ""}`}
              style={isActive("/admin/blogs") ? { background: "rgba(255, 255, 255, 0.15)", color: "white" } : {}}
            >
              Blogs
            </Link>
            <Link 
              to="/admin/opex-customers" 
              className={`nav-link ${isActive("/admin/opex-customers") ? "active" : ""}`}
              style={{
                ...(isActive("/admin/opex-customers") ? { background: "rgba(255, 255, 255, 0.15)", color: "white" } : {}),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                lineHeight: "1.2",
                padding: "0.5rem 0.75rem",
                fontSize: "0.8125rem",
                minWidth: "auto"
              }}
            >
              <span style={{ display: "block" }}>OPEX</span>
              <span style={{ display: "block" }}>Customers</span>
            </Link>
            <Link 
              to="/q" 
              className={`nav-link ${isActive("/q") ? "active" : ""}`}
              style={isActive("/q") ? { background: "rgba(255, 255, 255, 0.15)", color: "white" } : {}}
            >
              Sales View
            </Link>
            <button
              type="button"
              onTouchStart={(e) => {
                e.preventDefault();
                navigateTo("/q/leads", e);
              }}
              onClick={(e) => {
                navigateTo("/q/leads", e);
              }}
              className="nav-link admin-nav-button"
              style={{
                background: isActive("/q/leads") ? "rgba(139, 92, 246, 0.35)" : "rgba(139, 92, 246, 0.2)",
                color: "white",
                border: "1px solid rgba(139, 92, 246, 0.4)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                whiteSpace: "nowrap",
                touchAction: "manipulation"
              }}
              onMouseEnter={(e) => {
                if (!isActive("/q/leads")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.35)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.6)";
                }
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                if (!isActive("/q/leads")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.4)";
                }
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FiMap size={16} />
              <span className="admin-button-text-mobile">Find Leads</span>
            </button>
            <button
              type="button"
              onTouchStart={(e) => {
                e.preventDefault();
                navigateTo("/q/leads/shortlisted", e);
              }}
              onClick={(e) => {
                navigateTo("/q/leads/shortlisted", e);
              }}
              className="nav-link admin-nav-button"
              style={{
                background: isActive("/q/leads/shortlisted") ? "rgba(139, 92, 246, 0.35)" : "rgba(139, 92, 246, 0.2)",
                color: "white",
                border: "1px solid rgba(139, 92, 246, 0.4)",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                whiteSpace: "nowrap",
                touchAction: "manipulation"
              }}
              onMouseEnter={(e) => {
                if (!isActive("/q/leads/shortlisted")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.35)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.6)";
                }
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                if (!isActive("/q/leads/shortlisted")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.4)";
                }
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FiUsers size={16} />
              <span className="admin-button-text-mobile">Shortlisted</span>
            </button>
          </nav>
        )}

        <InstallAppButton />
      </div>
      
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
        {/* For sales agents: show navigation buttons on all pages (but not for content writers) */}
        {(!profile?.role || (profile?.role !== "admin" && profile?.role !== "contentwriter")) && (
          <>
            <button
              type="button"
              onTouchStart={(e) => {
                e.preventDefault();
                navigateTo("/q/leads/shortlisted", e);
              }}
              onClick={(e) => {
                navigateTo("/q/leads/shortlisted", e);
              }}
              className="btn-elegant btn-elegant-secondary topbar-nav-button"
              style={{ 
                padding: "0.5rem 1rem", 
                fontSize: "0.8125rem", 
                color: "white", 
                borderColor: "rgba(139, 92, 246, 0.4)",
                backgroundColor: isActive("/q/leads/shortlisted") ? "rgba(139, 92, 246, 0.35)" : "rgba(139, 92, 246, 0.2)",
                fontWeight: "600",
                minHeight: "48px",
                minWidth: "48px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                touchAction: "manipulation",
                whiteSpace: "nowrap"
              }}
              onMouseEnter={(e) => {
                if (!isActive("/q/leads/shortlisted")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.35)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.6)";
                }
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                if (!isActive("/q/leads/shortlisted")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.4)";
                }
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FiUsers size={16} />
              <span className="button-text-mobile">Shortlisted</span>
            </button>
            
            <button
              type="button"
              onTouchStart={(e) => {
                e.preventDefault();
                navigateTo("/q", e);
              }}
              onClick={(e) => {
                navigateTo("/q", e);
              }}
              className="btn-elegant btn-elegant-secondary topbar-nav-button"
              style={{ 
                padding: "0.5rem 1rem", 
                fontSize: "0.8125rem", 
                color: "white", 
                borderColor: "rgba(139, 92, 246, 0.4)",
                backgroundColor: isActive("/q") ? "rgba(139, 92, 246, 0.35)" : "rgba(139, 92, 246, 0.2)",
                fontWeight: "600",
                minHeight: "48px",
                minWidth: "48px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                touchAction: "manipulation",
                whiteSpace: "nowrap"
              }}
              onMouseEnter={(e) => {
                if (!isActive("/q")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.35)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.6)";
                }
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                if (!isActive("/q")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.4)";
                }
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FiFileText size={16} />
              <span className="button-text-mobile">Quotes</span>
            </button>
            
            <button
              type="button"
              onTouchStart={(e) => {
                e.preventDefault();
                navigateTo("/q/leads", e);
              }}
              onClick={(e) => {
                navigateTo("/q/leads", e);
              }}
              className="btn-elegant btn-elegant-secondary topbar-nav-button"
              style={{ 
                padding: "0.5rem 1rem", 
                fontSize: "0.8125rem", 
                color: "white", 
                borderColor: "rgba(139, 92, 246, 0.4)",
                backgroundColor: isActive("/q/leads") ? "rgba(139, 92, 246, 0.35)" : "rgba(139, 92, 246, 0.2)",
                fontWeight: "600",
                minHeight: "48px",
                minWidth: "48px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                touchAction: "manipulation",
                whiteSpace: "nowrap"
              }}
              onMouseEnter={(e) => {
                if (!isActive("/q/leads")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.35)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.6)";
                }
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(139, 92, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                if (!isActive("/q/leads")) {
                  e.currentTarget.style.background = "rgba(139, 92, 246, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.4)";
                }
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <FiMap size={16} />
              <span className="button-text-mobile">Find Leads</span>
            </button>
          </>
        )}

        {/* For content writers: show Create Blog button */}
        {profile?.role === "contentwriter" && (
          <button
            type="button"
            onClick={() => {
              // Scroll to top and trigger new blog creation
              window.scrollTo(0, 0);
              // Dispatch a custom event that BlogAdmin can listen to
              window.dispatchEvent(new CustomEvent('createNewBlog'));
            }}
            className="btn-elegant btn-elegant-accent"
            style={{ 
              padding: "0.5rem 1.25rem", 
              fontSize: "0.875rem", 
              color: "white", 
              fontWeight: "600",
              minHeight: "48px",
              minWidth: "48px",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem"
            }}
          >
            <FiFileText size={16} />
            Create Blog
          </button>
        )}
        
        <button 
          type="button"
          onClick={onLogout}
          className="btn-elegant btn-elegant-secondary"
          style={{ 
            padding: "0.5rem 1.25rem", 
            fontSize: "0.875rem", 
            color: "white", 
            borderColor: "rgba(255, 255, 255, 0.4)",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            fontWeight: "600",
            minHeight: "48px",
            minWidth: "48px",
            touchAction: "manipulation"
          }}
        >
          Logout
        </button>
      </div>
    </div>
  );
}


